/*
 * Copyright (c) 2018 - 2020 CalAmp Corp.  All Rights Reserved
 */
/* eslint-disable no-console */
import isArray from 'lodash.isarray';
import isObject from 'lodash.isobject';
import {contains} from '../utils/StringUtil';

/**
 * Defines the valid Log Levels that can be used when sending output to the Logging
 * API.
 */
export type LogLevel = 'info' | 'warn' | 'error' | 'log';

/**
 * Defines the prefix options that can be passed to logging functions. This allows
 * for customization of the prefix message and its color for individual log messages.
 */
export type PrefixOptions = {
  prefix?: string;
  color?: string;
};

/**
 * Defines the application prefix used in all log messages. This helps aid in
 * filtering logs by term e.g. '[CalAmp iOn]'.
 *
 * The value is statically defined (when using nextjs) by either NEXT_PUBLIC_APP_VERSION
 * in next.config, or defined via process.env.APP_NAME, and falling back to 'CalAmp iOn'
 * if neither are provided.
 */
export const APPLICATION = process.env.NEXT_PUBLIC_APP_NAME || process.env.APP_NAME || 'CalAmp iOn';

/**
 * Determines if the logger is enabled or not. This value must only be
 * set via the enable function.
 */
let _enabled = typeof console !== 'undefined' && process.env.NODE_ENV !== 'test';

/**
 * Enables / Disables the logger. Note, this value is to be set via a property
 * from the environments.json configuration, or another external means (such as
 * a query string parameter, an environment variable, etc.).
 *
 * ```typescript
 *
 *   import {enable} from '@ion/core/src/logger/logger';
 *   import {get} from '@ion/core/src/environment/environment';
 *
 *   enable(get('enableLogging'));
 * ```
 */
export const enable = (val: boolean, force = false) => {
  _enabled = (val && _enabled) || force;
  if (_enabled) {
    const header = 'CalAmp';
    const styles = 'font-weight: bold; font-size: 22px; color: #02ABCA;';
    console.log('%c---------------------------------', styles);
    console.log(`%c${!contains(APPLICATION, header) ? header : ''} ${APPLICATION}`, styles);
    console.log('%c---------------------------------', styles);
  }
};

/**
 * Provides a convenience for determining if application logging is currently
 * enabled. This utility is primarily intended for API specific loggers to
 * allow for omitting logging functionality if logging is disabled globally.
 *
 * @returns `true` if application monitoring is globally enabled, otherwise,
 *          `false`.
 */
export const isEnabled = (): boolean => _enabled;

/**
 * Defines the default logger, all public logging functions are to delegate
 * to this function.
 */
const logger = (msg: string, type: LogLevel = 'log', prefixOptions?: PrefixOptions) => {
  const prefix      = prefixOptions?.prefix || APPLICATION;
  const color       = prefixOptions?.color;
  const headerStyle = color
    ? `font-weight: bold;color: ${color};`
    : 'font-weight: bold;';
  console?.[type](`%c[${prefix}]:%c ${msg}`, headerStyle, 'font-weight: normal');
};

/**
 * Provides the single entry point for logging info in iOn Suite.
 *
 * ```typescript
 *
 *   import {info} from '@ion/core/src/logger/logger';
 *
 *   info('This is an Info Log ...', 'abc', 123);
 *   // [CalAmp iOn] This is an Info Log ..., ABC, 123
 *
 *   info('Custom prefix log', {prefix: 'MyApp', color: '#FF0000'});
 *   // [MyApp] Custom prefix log
 * ```
 */
export const info = (...args) => {
  if (_enabled) {
    const {prefixOptions, logArgs} = extractPrefixOptions(args);
    logger(formatArgs(...logArgs), 'info', prefixOptions);
  }
};

/**
 * Provides the single entry point for logging warnings in iOn Suite.
 *
 * ```typescript
 *
 *   import {warn} from '@ion/core/src/logger/logger';
 *
 *   warn('This is a Warning Log ...', 'abc', 123);
 *   // [CalAmp iOn] This is a Warning Log ..., ABC, 123
 *
 *   warn('Custom prefix warning', {prefix: 'MyApp'});
 *   // [MyApp] Custom prefix warning
 * ```
 */
export const warn = (...args) => {
  if (_enabled) {
    const {prefixOptions, logArgs} = extractPrefixOptions(args);
    logger(formatArgs(...logArgs), 'warn', prefixOptions);
  }
};

/**
 * Provides the single entry point for logging errors in iOn Suite.
 *
 * ```typescript
 *
 *   import {error} from '@ion/core/src/logger/logger';
 *
 *   error('This is an Error Log ...', 'abc', 123);
 *   // [CalAmp iOn] This is an Error Log ..., ABC, 123
 *
 *   error(new Error('This is an Error Log ...'), 'abc', 123);
 *   // [CalAmp iOn] Error: This is an Error Log ...
 *   //   at (stack trace) ...
 *   // ABC, 123
 *
 *   error('Custom error', {prefix: 'MyApp', color: '#FF0000'});
 *   // [MyApp] Custom error
 * ```
 */
export const error = (...args) => {
  if (_enabled) {
    const {prefixOptions, logArgs} = extractPrefixOptions(args);
    let processedArgs = logArgs;
    if (processedArgs[0] instanceof Error) {
      processedArgs = [processedArgs[0].stack, ...processedArgs.slice(1)];
    }
    logger(formatArgs(...processedArgs), 'error', prefixOptions);
  }
};

/**
 * Provides a convenience logger which allows for logging a value directly to the
 * console. Generally, this logger should be used for logging raw values directly
 * rather than having the values pre-formatted via the logger. This function is
 * essentially a wrapper around `console.log` with the exception that the call is
 * bypassed when logging is disable.
 *
 * ```typescript
 *
 *   import {info, log} from '@ion/core/src/logger/logger';
 *
 *   info('This is a Formatted Message');
 *   // [CalAmp iOn] This is a formatted message
 *
 *   log('This is an Unformatted Message');
 *   // This is an Unformatted Message
 *
 * ```
 */
export const log = (item) => {
  if (_enabled) {
    console.log(item);
  }
};

/**
 * Provides a convenience logger which allows for logging an arbitrary list
 * of values without pre-pending the log with the logger header. This utility
 * is generally provided for logging raw values.
 */
export const values = (...args) => {
  console.info(formatArgs(...args));
};

/**
 * Extracts `PrefixOptions` from any position in the arguments array, allowing
 * logging functions to accept custom prefix configurations on a per-call basis.
 *
 * The function identifies `PrefixOptions` by checking if any argument is an object
 * containing any of the expected `PrefixOptions` properties: prefix, or color.
 * Regular objects without these properties are treated as normal log arguments.
 *
 * ```typescript
 *   // basic usage with default prefix ...
 *   info('message', 'data', 123); // [CalAmp iOn] message data 123
 *
 *   // prefix options included at the end ...
 *   info('API Configuration Completed', {prefix: 'API Integrations'});
 *   // [API Integrations] API Configuration Completed
 *
 *   // prefix options included at the beginning ...
 *   warn({prefix: 'MyApp', color: '#04ff00ff'}, 'warning message');
 *   // [MyApp] warning message (in Green)
 *
 *   // prefix options in the middle ...
 *   error('error occurred', {prefix: 'API', color: '#FF0000'}, 'details', 500);
 *   // [API] error occurred details 500 (in red)
 *
 *   // regular object (not treated as prefix options) ...
 *   info('user data', {name: 'John', age: 30});
 *   // [CalAmp iOn] user data
 *   //   name: John
 *   //   age: 30
 *
 * ```
 *
 * @param   args The arguments array from logging functions
 * @returns Object containing separated prefixOptions and logArgs
 */
const extractPrefixOptions = (args: any[]) => {
  const prefixIndex = args.findIndex(arg =>
    isObject(arg) && (
      'prefix' in arg ||
      'color' in arg ||
      'omitHeader' in arg
    )
  );
  if (prefixIndex !== -1) {
    const prefixOptions = args[prefixIndex] as PrefixOptions;
    const logArgs       = [...args.slice(0, prefixIndex), ...args.slice(prefixIndex + 1)];
    return {logArgs, prefixOptions};
  }
  return {logArgs: args};
};

/**
 * Provides a simple utility for formatting logger arguments.
 */
const formatArgs = (...args) => {
  let message = '';
  args.forEach(arg => {
    if (isObject(arg)) {
      Object.keys(arg).forEach((prop, i) => {
        if (i === 0) {
          message += '\n';
        }
        message += `\t${prop}: ${arg[prop]}`;
        if (i < Object.keys(arg).length - 1) {
          message += '\n';
        }
      });
    } else if (isArray(arg)) {
      message += JSON.stringify(arg);
    } else {
      message += arg;
    }
    message += ' ';
  });
  return message.trim();
};
